import React from 'react';
import styled from 'styled-components';

// Partner pharmacy websites force generated SEO copy onto parts of the clinic
// components; the strings are computed per site by the web app (from pharmacy
// details), never stored here. These elements only make that visible to authors.

export const StyledSeoNote = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6b7280;
`;

const StyledLockedBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border: 1px dashed #c4c8ce;
  border-radius: 8px;
  background: #f8f9fa;
`;

const StyledLockedQuestion = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

const StyledLockedAnswer = styled.p`
  margin: 0;
  font-size: 13px;
  color: #4b5563;
`;

interface LockedFaq {
  question: string;
  answer: string;
}

const COLLECTION_FAQ: LockedFaq = {
  question: 'Can I collect my treatment from [pharmacy]?',
  answer:
    '"Yes. Once a clinician approves your request, you can collect the same day from [pharmacy] at ' +
    '[street], [town], or choose discreet UK delivery instead." — wording adapts to the pharmacy\'s ' +
    'collection/delivery setup (delivery-only sites ask "How will I receive my treatment?").',
};

const CONSULTATION_FAQ: LockedFaq = {
  question: 'How does the online consultation work?',
  answer:
    '"You answer a short set of medical questions about your health and the condition you want to treat. ' +
    'It takes around five minutes and there is nothing to pay until a clinician approves your request."',
};

const REVIEWER_FAQ: LockedFaq = {
  question: 'Who reviews my request?',
  answer:
    '"Every request is reviewed by a UK-registered clinician who checks that the treatment is safe and ' +
    'suitable for you. Requests are typically reviewed within one working day."',
};

const CONFIDENTIALITY_FAQ: LockedFaq = {
  question: 'Is this service confidential?',
  answer:
    '"Yes. Your consultation is private, your information is handled under UK data protection law, and ' +
    'deliveries arrive in plain packaging."',
};

// Must mirror the web app's forced sets (src/features/clinic/seo/localized-faqs.ts):
// clinic pages force all four; condition pages force the first and third only.
const LOCKED_FAQS: Record<'clinic' | 'condition', LockedFaq[]> = {
  clinic: [COLLECTION_FAQ, CONSULTATION_FAQ, REVIEWER_FAQ, CONFIDENTIALITY_FAQ],
  condition: [COLLECTION_FAQ, REVIEWER_FAQ],
};

export function SeoLockedFaqsNotice({ page }: { page: 'clinic' | 'condition' }) {
  const faqs = LOCKED_FAQS[page];
  return (
    <StyledLockedBox>
      <StyledSeoNote>
        🔒 Locked items — partner pharmacy websites always show these {faqs.length === 2 ? 'two' : 'four'} FAQs
        after the list below. They are generated per site from the pharmacy&apos;s own details and cannot be
        edited or removed here.
      </StyledSeoNote>
      {faqs.map((faq) => (
        <div key={faq.question}>
          <StyledLockedQuestion>{faq.question}</StyledLockedQuestion>
          <StyledLockedAnswer>{faq.answer}</StyledLockedAnswer>
        </div>
      ))}
    </StyledLockedBox>
  );
}
